import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface PageTransitionProps {
  children: React.ReactNode;
}

/**
 * Обёртка для анимации переходов между страницами.
 * 
 * Использует верхний сегмент пути как ключ (/sales, /purchase),
 * чтобы переключение этапов внутри раздела не вызывало повторную анимацию
 * (StagesLayout имеет свою собственную анимацию).
 */
export const PageTransition: React.FC<PageTransitionProps> = ({ children }) => {
  const location = useLocation();

  // Используем верхний сегмент пути как ключ анимации.
  // /sales/stage-1, /sales/stage-2 -> "/sales" (одна анимация на раздел).
  // /, /notifications, /settings -> уникальные ключи.
  const transitionKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    return segments.length > 0 ? `/${segments[0]}` : '/';
  }, [location.pathname]);

  const isHomePage = location.pathname === '/';

  const pageVariants = {
    initial: {
      opacity: 0,
      y: 16,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1] as const,
        opacity: { duration: 0.25 },
      }
    },
    exit: {
      opacity: 0,
      y: -10,
      transition: {
        duration: 0.2,
        ease: [0.4, 0, 1, 1] as const,
      }
    }
  };

  const noAnimationVariants = {
    initial: { opacity: 1 },
    animate: { opacity: 1 },
    exit: { opacity: 1 }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={transitionKey}
        variants={isHomePage ? noAnimationVariants : pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ width: '100%' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export default PageTransition;
